import {
  AppButton,
  AppCard,
  AppCheckbox,
  AppFormLabel,
  AppSelect,
  AppSpin,
  AppTypography,
} from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import { convertToIso, type VisitScheduleMassagerOption } from '../VisitSchedulePage.helpers';
import {
  VisitScheduleWeekCalendar,
  type VisitScheduleVisitDisplay,
} from './VisitScheduleWeekCalendar';

type VisitScheduleToolbarProps = {
  massagerLabel: string;
  massagerOptions: VisitScheduleMassagerOption[];
  selectedRowId?: number;
  visitdate: string;
  massagerId: number | null;
  isPrinting: boolean;
  onMassagerChange: (rowId: number | null) => void;
  onVisitDateChange: (visitdate: string) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onPrint: () => void;
};

export function VisitScheduleToolbar({
  massagerLabel,
  massagerOptions,
  selectedRowId,
  visitdate,
  massagerId,
  isPrinting,
  onMassagerChange,
  onVisitDateChange,
  onPrevWeek,
  onNextWeek,
  onPrint,
}: VisitScheduleToolbarProps) {
  return (
    <AppCard className="p-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center w-1/2 gap-2">
          <AppFormLabel label={massagerLabel} className="shrink-0" />
          <AppSelect
            options={massagerOptions}
            value={selectedRowId}
            onChange={v => onMassagerChange(v ? Number(v) : null)}
            className="flex-1"
            allowClear={false}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AppFormLabel label="訪問日" className="shrink-0" />
          <RA2NDatePicker
            value={visitdate || ''}
            onChange={val => {
              const iso = convertToIso(val);
              if (iso) onVisitDateChange(iso);
            }}
            className="w-[200px]"
          />
          <AppButton type="primary" className="!h-[40px] shrink-0" onClick={onPrevWeek}>
            &lt; 前週
          </AppButton>
          <AppButton type="primary" className="!h-[40px] shrink-0" onClick={onNextWeek}>
            翌週 &gt;
          </AppButton>
        </div>
        <AppButton
          type="default"
          className="!h-[40px] shrink-0"
          onClick={onPrint}
          loading={isPrinting}
          disabled={!massagerId || isPrinting}
        >
          印刷
        </AppButton>
      </div>
    </AppCard>
  );
}

type VisitScheduleCalendarPanelProps = {
  visitdate: string;
  visits: VisitScheduleVisitDisplay[];
  checkinEnabled: boolean;
  isCheckinEnabledForVisit: (visit: VisitScheduleVisitDisplay) => boolean;
  departurePositionText: string;
  copyWeekIncludeMemo: boolean;
  copyWeekLoading: boolean;
  onCopyWeekIncludeMemoChange: (checked: boolean) => void;
  onCopyWeek: () => void;
  onCellClick: (dateStr: string, timeStr: string, dayIndex: number, slotIndex: number) => void;
  onVisitClick: (visit: VisitScheduleVisitDisplay) => void;
  onVisitDrop: (visit: VisitScheduleVisitDisplay, dateStr: string, timeStr: string) => void;
  onVisitResize: (visit: VisitScheduleVisitDisplay, newMinutes: number) => void;
  onCopyDayClick: (dayStr: string) => void;
  onDeleteDayClick: (dayStr: string) => void;
  onDetailClick: (dayStr: string) => void;
};

export function VisitScheduleCalendarPanel({
  visitdate,
  visits,
  checkinEnabled,
  isCheckinEnabledForVisit,
  departurePositionText,
  copyWeekIncludeMemo,
  copyWeekLoading,
  onCopyWeekIncludeMemoChange,
  onCopyWeek,
  onCellClick,
  onVisitClick,
  onVisitDrop,
  onVisitResize,
  onCopyDayClick,
  onDeleteDayClick,
  onDetailClick,
}: VisitScheduleCalendarPanelProps) {
  return (
    <AppCard className="p-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-sm">
          <span className="text-gray-600">出発位置：</span>
          <span>{departurePositionText}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <AppCheckbox
            checked={copyWeekIncludeMemo}
            onChange={e => onCopyWeekIncludeMemoChange(e.target.checked)}
            className="text-sm"
          >
            メモを含む
          </AppCheckbox>
          <AppButton
            type="default"
            size="small"
            className="!h-8"
            onClick={onCopyWeek}
            loading={copyWeekLoading}
            disabled={copyWeekLoading}
          >
            次週にコピー
          </AppButton>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-gray-600">
        <span>
          <i className="fa fa-star" aria-hidden="true" />
          ＝訪問入力有
        </span>
        {checkinEnabled && (
          <>
            <span>
              <i className="text-blue-600 fa fa-map-marker" aria-hidden="true" />
              ＝施術中
            </span>
            <span>
              <i className="text-blue-600 fa fa-thumbs-o-up" aria-hidden="true" />
              ＝施術完了
            </span>
          </>
        )}
        <span>
          <i className="text-blue-600 fa fa-sticky-note-o" aria-hidden="true" />
          ＝施術メモ有
        </span>
      </div>
      <VisitScheduleWeekCalendar
        visitdate={visitdate}
        visits={visits}
        checkinEnabled={checkinEnabled}
        isCheckinEnabledForVisit={isCheckinEnabledForVisit}
        noCard
        onCellClick={onCellClick}
        onVisitClick={onVisitClick}
        onVisitDrop={onVisitDrop}
        onVisitResize={onVisitResize}
        onCopyDayClick={onCopyDayClick}
        onDeleteDayClick={onDeleteDayClick}
        onDetailClick={onDetailClick}
      />
    </AppCard>
  );
}

export function VisitScheduleLoading() {
  return (
    <div className="flex justify-center py-8">
      <AppSpin size="large" />
    </div>
  );
}

type VisitScheduleEmptyStateProps = {
  isR2: boolean;
};

export function VisitScheduleEmptyState({ isR2 }: VisitScheduleEmptyStateProps) {
  return (
    <AppCard className="p-6 text-center text-gray-800">
      {isR2
        ? 'マッサージ師が登録されていません。マスタメンテナンスで登録してください。'
        : 'はり・きゅう師が登録されていません。マスタメンテナンスで登録してください。'}
    </AppCard>
  );
}

type VisitScheduleSubmitBarProps = {
  isFullMonth: boolean;
  isSubmitting: boolean;
  onFullMonthChange: (checked: boolean) => void;
  onSubmit: () => void | Promise<boolean>;
};

export function VisitScheduleSubmitBar({
  isFullMonth,
  isSubmitting,
  onFullMonthChange,
  onSubmit,
}: VisitScheduleSubmitBarProps) {
  return (
    <BottomBar
      variant="action"
      visible
      fixed
      left={
        <AppTypography.Small className="max-w-full text-left text-white md:text-sm">
          ※データ修正後、エラー解消後、返戻処理後は、「当月再更新」にチェックしてデータを更新して下さい
        </AppTypography.Small>
      }
      right={
        <div className="flex items-center gap-4">
          <AppCheckbox
            checked={isFullMonth}
            onChange={e => onFullMonthChange(e.target.checked)}
            className="!text-white [&_.ant-checkbox-inner]:!border-white [&_.ant-checkbox+span]:!text-white"
          >
            当月再更新
          </AppCheckbox>
          <AppButton
            type="primary"
            className="font-medium rounded"
            onClick={onSubmit}
            loading={isSubmitting}
          >
            <i className="mr-2 fa fa-paper-plane" aria-hidden="true" />
            データ更新
          </AppButton>
        </div>
      }
    />
  );
}
